"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getBaseStyle = getBaseStyle;
exports.getColorStyle = getColorStyle;
exports.getEndEnhancerStyle = getEndEnhancerStyle;
exports.getLoadingSpinnerColorStyle = getLoadingSpinnerColorStyle;
exports.getLoadingSpinnerSizeStyle = getLoadingSpinnerSizeStyle;
exports.getSizeStyle = getSizeStyle;
exports.getSpinnerContainerStyle = getSpinnerContainerStyle;
exports.getStartEnhancerStyle = getStartEnhancerStyle;

var _color = _interopRequireDefault(require("color"));

var _style = require("../utils/style");

var _constants = require("./constants");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

// Baseui is using border shorthand, so we always
// need to override with border shorthand to get it to
// apply. https://github.com/uber-web/baseui/issues/1779
const borderStyle = 'solid';
const borderWidth = '2px';

function getBaseStyle({
  $theme
}) {
  const borderRadius = $theme.borders.radius200;
  return {
    position: 'relative',
    ...(0, _style.buildBorderRadiusStyle)(borderRadius),
    ...(0, _style.buildBorderStyle)('Style', borderStyle),
    ...(0, _style.buildBorderStyle)('Width', borderWidth),
    ':focus': {
      backgroundColor: ''
    },
    paddingTop: 0,
    paddingBottom: 0,
    letterSpacing: '0.015625rem',
    lineHeight: '1.5rem',
    // Baseui transitions 'background' by default, but we extend
    // the buttons with borders for consistency for the inverse buttons,
    // so we need to transition the border color in the same way.
    transitionProperty: 'background, border-color'
  };
}

function getSizeStyle(size) {
  switch (size) {
    case _constants.Size.SMALL:
      return {
        fontSize: '0.875rem',
        fontWeight: 700,
        paddingRight: '1rem',
        paddingLeft: '1rem',
        paddingTop: '0.125rem',
        paddingBottom: '0.125rem'
      };

    case _constants.Size.STANDARD:
      return {
        fontSize: '1rem',
        fontWeight: 700,
        paddingRight: '1.5rem',
        paddingLeft: '1.5rem',
        paddingTop: '0.625rem',
        paddingBottom: '0.625rem'
      };

    case _constants.Size.LARGE:
      return {
        fontSize: '1.5rem',
        fontWeight: 700,
        paddingRight: '1.75rem',
        paddingLeft: '1.75rem',
        paddingTop: '1.125rem',
        paddingBottom: '1.125rem'
      };

    default:
      return {};
  }
}

function getColorStyle(kind, styleArgs, disabledOverride) {
  const {
    $theme
  } = styleArgs;

  switch (kind) {
    case _constants.Kind.PRIMARY:
      return {
        backgroundColor: $theme.colors.primary400,
        borderColor: $theme.colors.primary400,
        ':hover:enabled': {
          backgroundColor: $theme.colors.primary300,
          borderColor: $theme.colors.primary300
        },
        ':active:enabled': {
          backgroundColor: $theme.colors.primary500,
          borderColor: $theme.colors.primary500
        },
        ':disabled': {
          color: disabledOverride ? $theme.colors.buttonPrimaryText : $theme.colors.grey50,
          backgroundColor: disabledOverride ? $theme.colors.primary400 : $theme.colors.grey10,
          borderColor: disabledOverride ? $theme.colors.primary400 : $theme.colors.grey10
        }
      };

    case _constants.Kind.PRIMARY_REVERSE:
      return {
        backgroundColor: $theme.colors.white,
        borderColor: $theme.colors.white,
        color: $theme.colors.primary400,
        ':hover:enabled': {
          backgroundColor: $theme.colors.primary50,
          borderColor: $theme.colors.primary50
        },
        ':active:enabled': {
          backgroundColor: $theme.colors.primary100,
          borderColor: $theme.colors.primary100
        },
        ':disabled': {
          color: disabledOverride ? $theme.colors.primary400 : $theme.colors.grey50,
          backgroundColor: disabledOverride ? $theme.colors.white : $theme.colors.grey10,
          borderColor: disabledOverride ? $theme.colors.white : $theme.colors.grey10
        }
      };

    case _constants.Kind.SECONDARY:
      return {
        backgroundColor: 'transparent',
        borderColor: $theme.colors.primary400,
        color: $theme.colors.primary400,
        ':hover:enabled': {
          backgroundColor: $theme.colors.primary50
        },
        ':active:enabled': {
          backgroundColor: $theme.colors.primary100
        },
        ':disabled': {
          backgroundColor: disabledOverride ? 'transparent' : $theme.colors.white,
          borderColor: disabledOverride ? $theme.colors.primary400 : $theme.colors.grey20,
          color: disabledOverride ? $theme.colors.primary400 : $theme.colors.grey30
        }
      };

    case _constants.Kind.TERTIARY:
      return {
        backgroundColor: 'transparent',
        borderColor: $theme.colors.grey40,
        color: $theme.colors.grey50,
        ':hover:enabled': {
          backgroundColor: $theme.colors.grey10
        },
        ':active:enabled': {
          backgroundColor: $theme.colors.grey20
        },
        ':disabled': {
          backgroundColor: 'transparent',
          borderColor: disabledOverride ? $theme.colors.grey40 : $theme.colors.grey20,
          color: disabledOverride ? $theme.colors.grey50 : $theme.colors.grey30
        }
      };

    case _constants.Kind.SECONDARY_REVERSE:
      return {
        backgroundColor: 'transparent',
        borderColor: $theme.colors.white,
        color: $theme.colors.white,
        ':hover:enabled': {
          backgroundColor: 'rgba(255, 255, 255, 0.2)'
        },
        ':active:enabled': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)'
        },
        ':disabled': {
          backgroundColor: 'transparent',
          opacity: 0.65,
          color: $theme.colors.white
        }
      };

    case _constants.Kind.MINIMAL:
      return {
        color: $theme.colors.primary400,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        ':hover:enabled': {
          backgroundColor: 'transparent',
          color: $theme.colors.primary200
        },
        ':active:enabled': {
          backgroundColor: 'transparent',
          color: $theme.colors.primary100
        },
        ':disabled': {
          backgroundColor: 'transparent',
          color: disabledOverride ? $theme.colors.primary400 : $theme.colors.grey40
        }
      };

    case _constants.Kind.MINIMAL_SECONDARY:
      return {
        color: $theme.colors.grey60,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        ':hover:enabled': {
          backgroundColor: 'transparent',
          color: $theme.colors.grey30
        },
        ':active:enabled': {
          backgroundColor: 'transparent',
          color: $theme.colors.grey20
        },
        ':disabled': {
          backgroundColor: 'transparent',
          color: $theme.colors.grey40
        }
      };

    default:
      return {};
  }
}

function getStartEnhancerStyle(size) {
  switch (size) {
    case _constants.Size.SMALL:
      return {
        marginRight: '0.5rem'
      };

    case _constants.Size.STANDARD:
      return {
        marginRight: '1rem'
      };

    case _constants.Size.LARGE:
      return {
        marginRight: '1rem'
      };

    default:
      return {};
  }
}

function getEndEnhancerStyle(size) {
  switch (size) {
    case _constants.Size.SMALL:
      return {
        marginLeft: '0.5rem'
      };

    case _constants.Size.STANDARD:
      return {
        marginLeft: '1rem'
      };

    case _constants.Size.LARGE:
      return {
        marginLeft: '1rem'
      };

    default:
      return {};
  }
}

function getLoadingSpinnerColorStyle(kind, styleArgs) {
  const {
    $theme
  } = styleArgs;

  const getBorderColors = color => {
    const background = (0, _color.default)(color).alpha(0.33).rgb().string();
    return {
      borderTopColor: color,
      borderRightColor: background,
      borderBottomColor: background,
      borderLeftColor: background
    };
  };

  switch (kind) {
    case _constants.Kind.PRIMARY:
      return getBorderColors($theme.colors.white);

    case _constants.Kind.PRIMARY_REVERSE:
      return getBorderColors($theme.colors.primary400);

    case _constants.Kind.SECONDARY:
      return getBorderColors($theme.colors.primary400);

    case _constants.Kind.SECONDARY_REVERSE:
      return getBorderColors($theme.colors.white);

    case _constants.Kind.MINIMAL:
      return getBorderColors($theme.colors.primary400);
  }

  return {};
}

function getLoadingSpinnerSizeStyle(size) {
  switch (size) {
    case _constants.Size.SMALL:
      return {
        height: '1rem',
        width: '1rem'
      };

    case _constants.Size.STANDARD:
      return {
        height: '1.5rem',
        width: '1.5rem'
      };

    case _constants.Size.LARGE:
      return {
        height: '2rem',
        width: '2rem'
      };
  }

  return {};
}

function getSpinnerContainerStyle() {
  return {
    marginTop: 0,
    marginBottom: 0,
    display: 'flex',
    height: '24px',
    justifyContent: 'center',
    alignItems: 'center'
  };
}